import React, { useMemo } from 'react'
import LeftWindowIcon from 'packages/@slerp/assets/integrations/left-wing.png'
import RightWindowIcon from 'packages/@slerp/assets/integrations/right-wing.png'
import styled from '@emotion/styled'
import { useQuery } from '@apollo/client'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FetchedIntegration, Integration, IntegrationStatus } from './types'
import IntegrationCard from './IntegrationCard'
import {
  INTEGRATION_CATEGORY_LABELS,
  INTEGRATION_REGISTRY,
  CUSTOM_WEBHOOK_SLUG,
  CUSTOM_WEBHOOK_CATEGORY
} from './constants'
import { GET_INTEGRATIONS } from './queries'
import Loading from 'components/Utils/Loading'
import ErrorMessage from 'components/Utils/ErrorMessage'
import { CommonButton } from 'components/Common'
import { useIntegration } from 'hooks/useGlobalContext/useIntegration'
import { CustomOrderWebhook } from './Manage/ManageCustomWebhook/types'

type IntegrationsByCategory = Record<string, Integration[]>

type GroupedIntegrations = {
  byCategory: IntegrationsByCategory
  uniqueCount: number
}

const CATEGORY_PARAM = 'category'

const categoryLabel = (category: string) =>
  INTEGRATION_CATEGORY_LABELS[category] ?? category

const compareByLabel = (a: string, b: string) =>
  categoryLabel(a).localeCompare(categoryLabel(b))

const groupIntegrations = (
  fetched: FetchedIntegration[]
): GroupedIntegrations => {
  const byCategory: IntegrationsByCategory = {}
  let uniqueCount = 0

  fetched.forEach((item) => {
    const registryEntry =
      INTEGRATION_REGISTRY[item.type as keyof typeof INTEGRATION_REGISTRY]
    if (!registryEntry) return

    const merged: Integration = {
      ...registryEntry,
      active: item.status === IntegrationStatus.ACTIVE,
      connectedStores: item.stores.connected.length,
      unconnectedStores: item.stores.notConnected.length
    }

    uniqueCount += 1

    item.category.forEach((category) => {
      if (!byCategory[category]) byCategory[category] = []
      byCategory[category].push(merged)
    })
  })

  return { byCategory, uniqueCount }
}

const webhookToIntegration = (webhook: CustomOrderWebhook): Integration => ({
  slug: `${CUSTOM_WEBHOOK_SLUG}-${webhook.id}`,
  name: webhook.name,
  description: webhook.url,
  active: webhook.enabled,
  connectedStores: 0,
  unconnectedStores: 0,
  to: `/integrations/${CUSTOM_WEBHOOK_SLUG}/edit/${webhook.id}`,
  inactiveActionLabel: 'Manage'
})

const Integrations = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const currentCategory = searchParams.get(CATEGORY_PARAM)

  const setCurrentCategory = (category: string | null) => {
    setSearchParams(category ? { [CATEGORY_PARAM]: category } : {})
  }

  const { data, loading, error } = useQuery<{
    getIntegrations: FetchedIntegration[]
  }>(GET_INTEGRATIONS, {
    fetchPolicy: 'no-cache'
  })

  const { customWebhooks: webhooks } = useIntegration()

  const { byCategory: integrations, uniqueCount } = useMemo(() => {
    const grouped = groupIntegrations(data?.getIntegrations ?? [])

    if (webhooks.length > 0) {
      grouped.byCategory[CUSTOM_WEBHOOK_CATEGORY] = [
        ...(grouped.byCategory[CUSTOM_WEBHOOK_CATEGORY] ?? []),
        ...webhooks.map(webhookToIntegration)
      ]
      grouped.uniqueCount += webhooks.length
    }

    return grouped
  }, [data, webhooks])

  const webhookCreateUrl = `/integrations/${CUSTOM_WEBHOOK_SLUG}/create`

  const sortedCategories = useMemo(
    () => Object.keys(integrations).sort(compareByLabel),
    [integrations]
  )

  const visibleCategories = currentCategory
    ? [currentCategory]
    : sortedCategories

  const renderBody = () => {
    if (loading) {
      return (
        <BodyArea>
          <Loading />
        </BodyArea>
      )
    }

    if (error) {
      return (
        <BodyArea>
          <ErrorMessage
            title='Could not load integrations'
            subheading='Please refresh the page or try again later.'
          />
        </BodyArea>
      )
    }

    return (
      <>
        <Sidebar>
          <CategoryTitle>Categories</CategoryTitle>
          <CategoryList>
            <CategoryEntry
              active={currentCategory === null}
              key='all'
              onClick={() => setCurrentCategory(null)}
            >
              <span>All</span>
              <span>{uniqueCount}</span>
            </CategoryEntry>
            {sortedCategories.map((category: string) => (
              <CategoryEntry
                active={currentCategory === category}
                key={category}
                onClick={() => setCurrentCategory(category)}
              >
                <span>{categoryLabel(category)}</span>
                <span>{integrations[category].length}</span>
              </CategoryEntry>
            ))}
          </CategoryList>
          <ConnectPanel>
            <ConnectTitle>Connect your own tools</ConnectTitle>
            <ConnectDescription>
              Send order events to any app or service — the moment an order is
              placed or updated.
            </ConnectDescription>
            <ConnectButton block onClick={() => navigate(webhookCreateUrl)}>
              Add custom webhook
            </ConnectButton>
          </ConnectPanel>
        </Sidebar>
        <Content>
          {visibleCategories.map((category) => {
            const categoryIntegrations = integrations[category] ?? []
            return (
              <CategorySection key={category}>
                <CategoryTitle>
                  {categoryLabel(category)}&nbsp;&nbsp;&nbsp;
                  {categoryIntegrations.length}
                </CategoryTitle>
                <IntegrationList>
                  {categoryIntegrations.map((integration) => (
                    <IntegrationCard
                      key={integration.slug}
                      integration={integration}
                    />
                  ))}
                </IntegrationList>
              </CategorySection>
            )
          })}
        </Content>
      </>
    )
  }

  return (
    <Root>
      <Header>
        <WingIcon src={LeftWindowIcon} />
        <TitleWrapper>
          <Title>Integrations to help you grow</Title>
          <Description>
            Our integrated partners offer services that seamlessly connect with
            Slerp, helping you run your business more efficiently across every
            touchpoint.
          </Description>
        </TitleWrapper>
        <WingIcon src={RightWindowIcon} />
      </Header>
      {renderBody()}
    </Root>
  )
}

const WingIcon = styled.img`
  height: 192px;
  object-fit: contain;
  object-position: center;
`

const Root = styled.div`
  display: grid;
  grid-template: 'header header' min-content 'sidebar content' 1fr / 248px 1fr;
  height: 100%;
`

const Header = styled.div`
  grid-area: header;

  width: calc(100% + 48px);
  margin-left: -24px;
  margin-right: -24px;
  overflow: hidden;

  display: flex;
  justify-content: space-between;
`

const TitleWrapper = styled.div`
  max-width: 700px;
  padding: 55px 0;
  text-align: center;
`

const Title = styled.h2`
  max-width: 700px;

  font-size: 28px;
  font-weight: 600;
  line-height: 100%;
  color: #4a4a68;
`

const Description = styled.p`
  max-width: 700px;

  font-size: 16px;
  font-weight: 500;
  line-height: 100%;
  color: #77778e;
`

const Sidebar = styled.div`
  grid-area: sidebar;

  margin-top: 24px;
  padding: 0 24px 24px 24px;
  border-right: 1px solid #f3f2f4;
`

const CategoryTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  line-height: 100%;
  color: #4a4a68;
`

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const CategoryEntry = styled.li<{ active: boolean }>(
  ({ active }) => `
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 4px 8px;
  margin-bottom: 8px;
  border-radius: 4px;

  font-size: 14px;
  font-weight: 500;
  line-height: 100%;
  color: ${active ? '#0A73E8' : '#4A4A68'};

  ${
    active &&
    `
    background-color: #F6FAFF;
  `
  }
`
)

const ConnectPanel = styled.div`
  margin-top: 24px;
  padding: 20px 10px;

  text-align: center;

  border: 1px solid #d2d2d9;
  border-radius: 4px;
`

const ConnectTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  line-height: 100%;
  text-transform: uppercase;
  color: #4a4a68;

  margin-bottom: 8px;
`

const ConnectDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;

  margin-bottom: 12px;
`

const ConnectButton = styled(CommonButton)`
  text-transform: uppercase;
`

const Content = styled.div`
  grid-area: content;

  padding: 24px;
`

const CategorySection = styled.div`
  margin-bottom: 32px;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const IntegrationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BodyArea = styled.div`
  grid-column: 1 / -1;
  grid-row: 2;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
`

export default Integrations
